import { apiJson } from './api';
import { LS } from './storage';

// 웹 푸시 구독: 앱·브라우저를 닫아도 멘션·채널 알림을 받는다.
// 아이폰/아이패드는 "홈 화면에 추가"한 앱에서만 지원된다 (iOS 16.4+).
const PREF_KEY = 'push_enabled';

const isIos = () => /iPhone|iPad|iPod/i.test(navigator.userAgent);
const isStandalone = () => window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;

export const pushSupport = () => {
    if (!('serviceWorker' in navigator)) return { supported: false, reason: '이 브라우저는 푸시 알림을 지원하지 않습니다.' };
    if (!('PushManager' in window) || !('Notification' in window)) {
        return isIos() && !isStandalone()
            ? { supported: false, reason: '아이폰은 공유 버튼 → "홈 화면에 추가"로 앱을 설치한 뒤 켤 수 있습니다.' }
            : { supported: false, reason: '이 브라우저는 푸시 알림을 지원하지 않습니다.' };
    }
    if (Notification.permission === 'denied') return { supported: true, denied: true, reason: '알림이 차단되어 있습니다. 브라우저/앱 설정에서 알림을 허용해 주세요.' };
    return { supported: true };
};

const toUint8Array = (base64) => {
    const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(padded);
    return Uint8Array.from(raw, ch => ch.charCodeAt(0));
};

const registration = async () => {
    const reg = await navigator.serviceWorker.getRegistration();
    if (reg) return reg;
    return navigator.serviceWorker.register('/sw.js');
};

// 기본값은 "켜짐". 단, 실제로 켜지는 것은 브라우저 알림 권한이 이미 허용된 기기뿐이다.
// (권한이 없으면 isPushOn() 이 false → 스위치는 꺼짐으로 보이고, 묻지도 않는다.
//  사용자가 직접 스위치를 켤 때만 권한을 요청한다 → enablePush)
export const isPushWanted = () => LS.get(PREF_KEY) !== 'false';

// 이 기기에서 사이트 알림 권한이 허용돼 있는가
export const hasNotificationPermission = () =>
    typeof Notification !== 'undefined' && Notification.permission === 'granted';

// 화면에 "켜짐"으로 보여야 하는가 = 사용자가 끄지 않았고 + 알림 권한도 허용돼 있다
export const isPushOn = () => isPushWanted() && hasNotificationPermission();

// 사용자가 스위치를 켤 때만 호출한다. 알림 권한이 없으면 여기서 한 번 더 물어보고,
// 허용되지 않으면 예외를 던져 절대 켜지지 않게 한다.
export async function enablePush() {
    const support = pushSupport();
    if (!support.supported || support.denied) throw new Error(support.reason);
    if (Notification.permission !== 'granted') {
        let granted = false;
        try { granted = (await Notification.requestPermission()) === 'granted'; } catch { granted = false; }
        if (!granted) throw new Error('사이트 알림 권한이 허용되지 않아 푸시 알림을 켤 수 없습니다. 브라우저 설정에서 알림을 허용해 주세요.');
    }
    const { key, enabled } = await apiJson('/api/push/public-key');
    if (!enabled || !key) throw new Error('서버에서 푸시 알림을 사용할 수 없습니다.');
    const reg = await registration();
    await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    // 서버 키가 바뀌었으면 새로 구독
    if (sub && sub.options?.applicationServerKey) {
        const current = btoa(String.fromCharCode(...new Uint8Array(sub.options.applicationServerKey))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
        if (current !== key) { await sub.unsubscribe().catch(() => {}); sub = null; }
    }
    if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toUint8Array(key) });
    await apiJson('/api/push/subscribe', { method: 'POST', json: { subscription: sub.toJSON() } });
    LS.set(PREF_KEY, 'true');
    return true;
}

export async function disablePush() {
    LS.set(PREF_KEY, 'false');
    try {
        const reg = await navigator.serviceWorker.getRegistration();
        const sub = await reg?.pushManager.getSubscription();
        if (sub) {
            await apiJson('/api/push/unsubscribe', { method: 'POST', json: { endpoint: sub.endpoint } }).catch(() => {});
            await sub.unsubscribe();
        }
    } catch {}
}

// 켜 둔 사용자는 접속할 때마다 구독을 서버와 다시 맞춘다 (기기 구독이 갱신·만료되는 경우 대비).
// 알림 권한이 이미 허용된 기기에서만 동작한다 → 접속만으로 권한 창이 뜨는 일은 없다.
// @returns {Promise<boolean>} 실제로 구독이 켜져 있는지
export async function syncPushSubscription() {
    if (!isPushOn() || !pushSupport().supported) return false;
    try { await enablePush(); return true; } catch { return false; }
}

export const sendTestPush = () => apiJson('/api/push/test', { method: 'POST' });
